/**
 * Cross-tab session synchronisation helper.
 *
 * Wraps a `BroadcastChannel` on a fixed channel name so all tabs of the same
 * origin can coordinate. Today we only use it for `{ type: 'logout' }` — when
 * one tab logs out, every other tab clears its in-memory vault + JWT so its
 * MobX-reactive UI immediately reflects the logged-out state.
 *
 * The channel name must match `BROADCAST_CHANNEL_NAME` in
 * `packages/embed-sdk/src/protocol.ts` ('scp-wallet-v2'). Defined here as a
 * local constant rather than imported to avoid a circular dep between
 * `@cef-ai/wallet-identity` and `@cef-ai/wallet`. The name is part of the
 * wire protocol — if you change one, change the other (and bump the major).
 *
 * Spec §9.4 (cross-tab session sharing).
 */

/** MUST match `packages/embed-sdk/src/protocol.ts` `BROADCAST_CHANNEL_NAME`. */
const CHANNEL_NAME = 'scp-wallet-v2';

export type CrossTabMessage = { type: 'logout' };

export class CrossTabSync {
  private channel: BroadcastChannel | null;
  private readonly listeners = new Set<(msg: CrossTabMessage) => void>();

  constructor() {
    // SSR / Node-without-BroadcastChannel safety. The class becomes a no-op
    // (broadcast() returns silently, subscribe() returns a real unsub that
    // just empties the local set, close() is a no-op).
    if (typeof BroadcastChannel === 'undefined') {
      this.channel = null;
      return;
    }
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (ev: MessageEvent) => {
      const msg = ev.data as CrossTabMessage | undefined;
      if (!msg || typeof msg !== 'object' || typeof (msg as { type?: unknown }).type !== 'string') {
        return;
      }
      for (const fn of this.listeners) {
        try {
          fn(msg);
        } catch {
          // Listener errors must not break the channel for other listeners.
        }
      }
    };
  }

  /** Broadcast a message to all other tabs (and not to this tab — that's the
   * BroadcastChannel spec). The originating tab is expected to have already
   * applied the state change locally before broadcasting. */
  broadcast(msg: CrossTabMessage): void {
    this.channel?.postMessage(msg);
  }

  /** Subscribe to messages from other tabs. Returns an unsubscribe function. */
  subscribe(fn: (msg: CrossTabMessage) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Close the underlying channel and drop all listeners. Idempotent.
   *
   * Sets `this.channel = null` so subsequent `broadcast()` calls become a
   * no-op (the `?.postMessage` short-circuits) and we don't accidentally
   * postMessage on a closed BroadcastChannel (which throws InvalidStateError
   * in some implementations).
   */
  close(): void {
    this.channel?.close();
    this.channel = null;
    this.listeners.clear();
  }
}
